import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { HTMLProps } from 'react';
import { IoLogOutOutline } from 'react-icons/io5';
import { Link } from 'react-router-dom';
import { LogoIcon } from '../../../assets/svg/logo';
import { Collapse } from '../../../components/collapse/collapse';
import { Version } from '../../../features/version/version';
import { hasService } from '../../../helpers/local-storage';
import { useManagerStore } from '../../../store/use-manager-store';
import s from './navigation.module.css';
import { useNavigation } from './use-navigation';

export const Navigation = (props: HTMLProps<HTMLDivElement>) => {
  const { className, ...other } = props;
  const {
    path1,
    path2,
    t,
    navigationTabs,
    navigate,
    selected,
    handleSelected,
    search,
    addQueryParams,
    isMobileOpen,
    handleLogout,
  } = useNavigation();
  const { hasAccess } = useManagerStore();

  const renderNav = () => (
    <ul className={s.list}>
      {navigationTabs
        ?.filter((item) => (item.service ? hasService(item.service) : true))
        .filter((item) =>
          item.permissionsService ? hasAccess(item.permissionsService, ['read', 'write']) : true,
        )
        .map((item) => {
          const isCurrentPath = item.href?.split('/').some((item) => item === path1);
          const isSelectedSubMenu = item.subMenu?.some((subItem) => subItem.href?.includes(path2));
          const isSelected = isCurrentPath || isSelectedSubMenu;
          const testId = `nav-${item.id}`;

          return (
            <Collapse
              id={testId}
              key={item.id}
              motionKey={item.id}
              toggleOpen={() => handleSelected(item.id)}
              open={selected === item.id}
              wrapperClassname={s.wrapper}
              withArrow={!!item.subMenu?.length}
              headerClassName={cn(s.item, isSelected && s.selected)}
              header={
                <div className={s.label}>
                  {item.icon}
                  {t(item.value)}
                </div>
              }
              onClickHeader={() => {
                if (item.href) navigate(item.href);
              }}
              childrenClassname={cn(s.subMenu, selected === item.id && s.open)}
              children={item.subMenu
                ?.filter((item) => (item.service ? hasService(item.service) : true))
                .map((subItem) => {
                  const isSelected = subItem.href?.includes(path2);
                  let uri = subItem.href!;

                  const isDashboardUris =
                    uri.includes('/manager/dashboard') && path1 === 'dashboard';
                  const isReviewUris = uri.includes('/manager/reviews') && path1 === 'reviews';
                  const isTasksUris = uri.includes('/manager/tasks') && path1 === 'tasks';

                  const isKeepParams = isDashboardUris || isReviewUris || isTasksUris;
                  if (isKeepParams) {
                    const searchWithoutPage = search
                      .replace(/page=\d+&?/, '')
                      .replace(/pageSize=\d+&?/, '');
                    uri = uri + (searchWithoutPage || subItem.defaultParams || '');
                  } else {
                    uri += subItem.defaultParams ? subItem.defaultParams : '';
                  }

                  return (
                    <Link
                      key={subItem.id}
                      data-testid={`${testId}-${subItem.id}`}
                      to={uri}
                      className={cn(s.subItem, isSelected && s.selected)}
                    >
                      <div className={s.label}>
                        <span className={s.dot} />
                        {t(subItem.value)}
                      </div>
                    </Link>
                  );
                })}
            />
          );
        })}
    </ul>
  );

  return (
    <>
      <nav className={cn(s.feature, className, 'hide__M hide__S')} {...other}>
        <LogoIcon className={s.logo} />
        {renderNav()}
        <Version className={s.version} />
      </nav>

      <AnimatePresence>
        {isMobileOpen && (
          <motion.nav
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'tween', stiffness: 300, damping: 30 }}
            className={s.mobileNav}
          >
            <div className={s.mobileWrapper}>
              <LogoIcon className={s.logo} />
              {renderNav()}

              <div className={s.logout}>
                <div className={s.label} onClick={handleLogout}>
                  <IoLogOutOutline size={24} />
                  {t('Выйти')}
                </div>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            className={s.backdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{ type: 'tween', stiffness: 300, damping: 30 }}
            onClick={() => addQueryParams({ nav: undefined })}
          />
        )}
      </AnimatePresence>
    </>
  );
};
